import type { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";

import type { OffersBlock as OffersBlockValue } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

type Value = OffersBlockValue;
type Offer = NonNullable<Value["offers"]>[number];

/**
 * Faithful port of the legacy server-rendered ``cms/blocks/offers-block.html``.
 *
 * All richtext fields (``header_content`` and each offer's ``lead_in``,
 * ``title``, ``subtitle``, ``content``) were rendered via ``{% include_block %}``,
 * which emits ``<div class="rich-text">``-wrapped HTML. The module targets
 * these with a CHILD combinator (``.offersBlock > .header``) and a
 * ``:nth-of-type(3)`` rule (``.offer .offerCopy:nth-of-type(3)``), both of
 * which require each wrapper to be a DIRECT child of its container -- so the
 * block serializes each field as BARE inner HTML (``offers.py``
 * ``get_data_for_client_render`` / ``expand_db_html``) and the wrapper is added
 * here via ``<RichTextBlock>`` carrying the module class beside ``rich-text``
 * (kept for the global RichText styles): exactly one div, not the double
 * wrapper a pre-wrapped value + ``RichTextBlock`` would produce. Empty
 * optional fields serialize to ``""`` and render an empty wrapper div
 * (matching the legacy empty-field render).
 *
 * The support icon uses the ``width-50`` rendition (legacy
 * ``{% wagtail_image offer.support_icon specs='width-50' class='icon' %}``) and
 * is omitted entirely when no icon is set (legacy ``{% wagtail_image %}`` of a
 * ``None`` image renders nothing).
 */
const OfferElem = (props: Immutable<{ offer: Offer }>): React.JSX.Element => {
    const offer = props.offer;
    const icon = offer.support_icon;
    const copyClassName = concatClassNames([styles.offerCopy, "rich-text"]);
    return (
        <div className={styles.offer}>
            {icon ? (
                <img
                    className={styles.icon}
                    src={icon.renditions["width-50"]?.url ?? icon.url}
                    alt={icon.title}
                    loading="lazy"
                    decoding="async"
                />
            ) : null}

            <RichTextBlock
                value={offer.lead_in ?? ""}
                className={copyClassName}
            />

            <div className={styles.title}>
                <RichTextBlock
                    value={offer.title ?? ""}
                    className={copyClassName}
                />
            </div>

            <RichTextBlock
                value={offer.subtitle ?? ""}
                className={copyClassName}
            />

            <RichTextBlock
                value={offer.content ?? ""}
                className={copyClassName}
            />
        </div>
    );
};

export const OffersBlock = (
    props: Immutable<{ value: OffersBlockValue }>,
): React.JSX.Element => {
    const v = props.value;
    return (
        <section
            className={concatClassNames([
                styles.offersBlock,
                getGlobalSpacingClasses(v.spacing),
            ])}
        >
            <RichTextBlock
                value={v.header_content ?? ""}
                className={concatClassNames([styles.header, "rich-text"])}
            />

            {(v.offers ?? []).map((offer, i) => (
                <OfferElem key={i} offer={offer} />
            ))}
        </section>
    );
};
